import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/portfolioCalculations";
import { Lock, LockOpen } from "lucide-react";

interface OverflowDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentSize: number;
  requiredSize: number;
  onAutoSize: (targetSize: number) => void;
  onKeepCurrent: () => void;
}

export const OverflowDialog = ({
  isOpen,
  onOpenChange,
  currentSize,
  requiredSize,
  onAutoSize,
  onKeepCurrent,
}: OverflowDialogProps) => {
  const handleAutoSize = () => {
    if (import.meta.env.DEV) {
      console.debug('[OverflowDialog] Auto-size clicked', {
        currentSize,
        requiredSize
      });
    }
    onAutoSize(requiredSize);
    onOpenChange(false);
    // Remember user chose auto-size for this session
    sessionStorage.setItem('portfolio-overflow-preference', 'auto-size');
  };

  const handleKeepCurrent = () => {
    onKeepCurrent();
    onOpenChange(false);
    // Remember user chose to keep current for this session
    sessionStorage.setItem('portfolio-overflow-preference', 'keep-current');
  };

  const overflowAmount = requiredSize - currentSize;
  const overflowPercentage = ((overflowAmount / currentSize) * 100).toFixed(1);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Portfolio Size Overflow
          </DialogTitle>
          <DialogDescription className="space-y-3 text-sm">
            <p>
              Your allocations total <strong>{formatCurrency(requiredSize)}</strong>, 
              which exceeds your portfolio size of <strong>{formatCurrency(currentSize)}</strong>{' '}
              by <strong>{formatCurrency(overflowAmount)}</strong> ({overflowPercentage}%).
            </p>
            <div className="bg-muted/50 p-3 rounded-lg space-y-2">
              <p className="font-medium text-foreground">What would you like to do?</p>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>• <strong>Auto-size</strong>: Expand portfolio to fit all allocations</p>
                <p>• <strong>Keep current</strong>: Maintain portfolio size (allocations may exceed 100%)</p>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg">
              <div className="flex items-start gap-2">
                <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Lock className="h-3 w-3" />
                  <LockOpen className="h-3 w-3" />
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  <strong>Tip:</strong> Use the lock toggle to control automatic portfolio expansion. 
                  When unlocked, your portfolio can auto-expand to fit allocations.
                </p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleKeepCurrent}
            className="flex-1"
          >
            Keep at {formatCurrency(currentSize)}
          </Button>
          <Button
            type="button"
            onClick={handleAutoSize}
            className="flex-1"
          >
            Auto-size to {formatCurrency(requiredSize)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
